import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Box,
  Table,
  Container,
} from "@cloudscape-design/components";
import { filterWorkflowsByTag } from "../../utils/workflowHelpers";
import { sanitizeForDisplay, validateRequiredIds } from "../../utils/security";
import { logger } from "../../utils/logger";
import Badge from "../common/Badge";
import Button from "../common/Button";
import { TrajectoryLoader } from "../common/Loaders";
import { ConfirmationModal } from "../common/ConfirmationModal";
import { RunWorkflowModal } from "../common/RunWorkflowModal";
import { Workflow } from "@amzn/finspace-service-typescript-client";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import "./workflows-page.scss";

interface WorkflowsPageProps {
  tag?: string;
}

const WorkflowsPage: React.FC<WorkflowsPageProps> = ({ tag }) => {
  const navigate = useNavigate();
  const { selectedSpaceId, fetchSpaces } = useGlobalStore();
  const { workflows, isLoading, isExecuting, isDeleting, fetchWorkflows, executeWorkflow, deleteWorkflow } = useWorkflowStore();
  
  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean;
    workflow: Workflow | null;
  }>({ visible: false, workflow: null });
  
  const [runModal, setRunModal] = useState<{
    visible: boolean;
    workflow: Workflow | null;
  }>({ visible: false, workflow: null });
  
  // Fetch spaces and workflows on mount
  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflows(selectedSpaceId);
    }
  }, [selectedSpaceId, fetchWorkflows]);
  
  const filteredWorkflows = filterWorkflowsByTag(workflows, tag);

  const pageTitle = tag 
    ? `${tag} Workflows` 
    : 'All Workflows';

  const handleRun = async (workflow: Workflow) => {
    setRunModal({ visible: true, workflow });
  };

  const handleRunConfirm = async (periods: string) => {
    const { workflow } = runModal;
    if (!workflow || !validateRequiredIds(selectedSpaceId, workflow.id)) {
      logger.error('Missing required IDs for workflow execution');
      setRunModal({ visible: false, workflow: null });
      return;
    }
    
    try {
      await executeWorkflow({
        spaceId: selectedSpaceId!,
        workflowId: workflow.id!,
        input: { periods }
      });
      setRunModal({ visible: false, workflow: null });
    } catch (error) {
      logger.error('Failed to execute workflow:', error);
    }
  };

  const handleRunCancel = () => {
    setRunModal({ visible: false, workflow: null });
  };

  const handleEdit = (workflow: Workflow) => {
    // Check if this is an MFR workflow
    if (workflow.tags?.includes('MFR')) {
      // Navigate to MFR edit page with workflow ID
      navigate(`/reports-and-analysis/mfr/edit/${workflow.id}`);
    } else {
      // For other workflow types, show a message or implement their edit pages
      logger.log('Edit workflow:', workflow);
    }
  };

  const handleDeleteClick = (workflow: Workflow) => {
    setDeleteModal({ visible: true, workflow });
  };

  const handleDeleteConfirm = async () => {
    const { workflow } = deleteModal;
    if (!workflow || !validateRequiredIds(selectedSpaceId, workflow.id)) {
      logger.error('Missing required IDs for workflow deletion');
      setDeleteModal({ visible: false, workflow: null });
      return;
    }

    try {
      await deleteWorkflow(selectedSpaceId!, workflow.id!);
      setDeleteModal({ visible: false, workflow: null });
    } catch (error) {
      logger.error('Failed to delete workflow:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ visible: false, workflow: null });
  };



  const columnDefinitions = [
    {
      id: "workflow",
      header: "Workflow",
      cell: (workflow: Workflow) => (
        <Box>
          <div className="workflow-name">
            {workflow.name}
          </div>
          {workflow.description && (
            <div className="workflow-description">
              {workflow.description}
            </div>
          )}
        </Box>
      ),
      width: "40%",
      minWidth: "280px",
    },
    {
      id: "createdDate",
      header: "Created Date",
      cell: (workflow: Workflow) => 
        (workflow as any).createdTime ? new Date((workflow as any).createdTime).toLocaleDateString() : 'N/A',
      width: "20%",
      minWidth: "70px",
    },
    {
      id: "status",
      header: "Status",
      cell: (workflow: Workflow) => (
        <Badge variant="status" color="green">
          Active
        </Badge>
      ),
      width: "15%",
      minWidth: "70px",
    },
    {
      id: "actions",
      header: "Actions",
      cell: (workflow: Workflow) => (
        <div className="workflow-actions">
          <Button
            variant="primary"
            icon="play"
            onClick={() => handleRun(workflow)}
            loading={isExecuting}
          >
            Run
          </Button>
          
          <Button
            variant="secondary"
            icon="edit"
            onClick={() => handleEdit(workflow)}
          >
            Edit
          </Button>
          
          <Button
            variant="secondary"
            icon="remove"
            onClick={() => handleDeleteClick(workflow)}
            className="workflow-delete-button"
            loading={isDeleting}
          >
            Delete
          </Button>
        </div>
      ),
      width: "30%",
      minWidth: "280px",
    },
  ];

  if (isLoading) {
    return (
      <ContentLayout defaultPadding>
        <div className="loading-state">
          <TrajectoryLoader size="140px" speed="2s" />
          <div className="loading-message">
            Loading workflows...
          </div>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      defaultPadding
      header={<Header variant="h1">{pageTitle}</Header>}
    >
      <SpaceBetween direction="vertical" size="l">
        <Container>
          <Table
            columnDefinitions={columnDefinitions}
            items={filteredWorkflows}
            loading={isLoading}
            loadingText="Loading workflows..."
            empty={
              <Box textAlign="center" color="inherit">
                <b>No workflows found</b>
                <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                  {tag
                    ? `No workflows have been created from ${tag} template yet.`
                    : 'No workflows have been created yet.'
                  }
                </Box>
              </Box>
            }
            variant="borderless"
          />
        </Container>
      </SpaceBetween>

      <ConfirmationModal
        visible={deleteModal.visible}
        title="Delete Workflow"
        message={`Are you sure you want to delete workflow "${sanitizeForDisplay(deleteModal.workflow?.name)}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="primary"
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      <RunWorkflowModal
        visible={runModal.visible}
        workflow={runModal.workflow}
        loading={isExecuting}
        onRun={handleRunConfirm}
        onCancel={handleRunCancel}
      />
    </ContentLayout>
  );
};

export default WorkflowsPage;